import { css, cx } from '@linaria/atomic';
import { motion } from 'motion/react';
import { useBreakpoint } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { PANEL_MIN_WIDTH } from '../const';
import { ContentPanelProps } from '../types';
import { ContentPanelBody } from './content-body';
import { ContentPanelBreadcrumbs } from './content-breadcrumbs';
import { ContextValue, ContentPanelContext } from './content-context';
import { ContentPanelHeader } from './content-header';

const contentPanelMotionSectionStyle1 = css`
  min-width: min(${PANEL_MIN_WIDTH}px, 100%);
  display: flex;
  flex-direction: column;
  height: 100%;
  container-type: inline-size;
  &:last-of-type {
    width: 100%;
  }
  &:not(:first-of-type) {
    border-left: 1px solid ${theme.colors.neutral20};
  }
`;

const defaultVariants: ContentPanelProps['variants'] = {
  xsmall: {
    padded: css`
      padding: ${theme.spacing(1)};
    `,
    reference: css`
      padding: ${theme.spacing(4)};
    `,
    none: css``,
  },
  small: {
    padded: css`
      padding: ${theme.spacing(3)};
    `,
    reference: css`
      padding: ${theme.spacing(4)};
    `,
    none: css``,
  },
  medium: {
    padded: css`
      padding: ${theme.spacing(5)};
    `,
    reference: css`
      padding: ${theme.spacing(4)};
    `,
    none: css``,
  },
  large: {
    padded: css`
      padding: ${theme.spacing(5)};
    `,
    reference: css`
      padding: ${theme.spacing(4)};
    `,
    none: css``,
  },
  mobile: {
    padded: css`
      padding: ${theme.spacing(5)};
    `,
    reference: css`
      padding: ${theme.spacing(4)};
    `,
    none: css``,
  },
  xlarge: {
    padded: css`
      padding: ${theme.spacing(5)};
    `,
    reference: css`
      padding: ${theme.spacing(4)};
    `,
    none: css``,
  },
};

export const ContentPanel = ({
  children,
  id,
  isActive,
  isHidden,
  hasNav,
  hasBack,
  direction,
  shouldAnimate,
  onBackClick,
  variant = 'none',
  variants = defaultVariants,
  ...rest
}: ContentPanelProps) => {
  const value: ContextValue = {
    id,
    isActive,
    isHidden,
    hasNav,
    hasBack,
    onBackClick,
  };
  const breakpoint = useBreakpoint();
  const additionalStyles = variants?.[breakpoint]?.[variant];

  return (
    <ContentPanelContext.Provider value={value}>
      <motion.section
        initial={shouldAnimate && { x: direction === 'left' ? '-100%' : '100%' }}
        animate={shouldAnimate && { x: '0%' }}
        exit={shouldAnimate ? { x: direction === 'left' ? '-100%' : '100%' } : undefined}
        transition={{
          duration: 0.2,
          ease: 'easeIn',
        }}
        {...rest}
        className={cx(contentPanelMotionSectionStyle1, additionalStyles, rest.className)}
      >
        {/* Remove this once beta period is complete */}
        {children}
      </motion.section>
    </ContentPanelContext.Provider>
  );
};

ContentPanel.Header = ContentPanelHeader;
ContentPanel.Body = ContentPanelBody;
ContentPanel.Breadcrumbs = ContentPanelBreadcrumbs;
